// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's keymap (frontend-u0o.9): one registry of every
 * command — the tool dock's tools, the selection bar's modify commands,
 * Slet, Tilbage til detekteret, Fortryd / Gentag, the views — with its key,
 * label and search words. The keyboard, the dock's and bar's tooltips and
 * the ⌘K palette all read it, so a command is reachable the same way from
 * everywhere ("forlæng" finds Forlæng/trim with its TR).
 *
 * Keys: single letters pick tools (SketchUp); the two-letter codes (Revit:
 * MV, CO, RO, MM, OF, SL, JN, TR, AL; SF for Sammenføj) work everywhere. A letter that starts a
 * code waits a moment for the second one (`resolveKeys` says so with
 * `pending`) before it counts as the letter alone. Pure: no React, no DOM.
 */

import type { TargetKind } from '../app/keyTargets';

export type GeometriTool = 'select' | 'wall' | 'floor' | 'room' | 'zone' | 'door' | 'window' | 'opening' | 'stair' | 'measure' | 'dimension';

export type GeometriCommand =
  | `tool:${GeometriTool}`
  | 'move'
  | 'copy'
  | 'rotate'
  | 'mirror'
  | 'offset'
  | 'split'
  | 'join'
  | 'merge'
  | 'trim'
  | 'align'
  | 'revert'
  | 'delete'
  | 'undo'
  | 'redo'
  | 'escape'
  | 'cycle'
  | 'cycle-back'
  | 'view:plan'
  | 'view:3d'
  | 'zoom-fit'
  | 'rederive';

export interface GeometriCommandDef {
  command: GeometriCommand;
  label: string;
  /** How the key reads in a tooltip or the palette's hint ("W", "MV", "⌘Z"); '' = none. */
  keys: string;
  /** Extra words ⌘K matches. */
  keywords: string;
  /** Available in this build (the others show disabled, "kommer snart"). */
  ready: boolean;
  /** The slice that brings it, for the "kommer snart" tooltip. */
  later?: string;
  /** Only with a selection. */
  needsSelection?: boolean;
  /** Writes to the model (left out for a viewer). */
  write?: boolean;
}

/** The tool dock's tools, in dock order, grouped as the dock draws them. */
export const TOOL_GROUPS: readonly (readonly GeometriTool[])[] = [
  ['select'],
  ['wall', 'floor', 'room', 'zone'],
  ['door', 'window', 'opening', 'stair'],
  ['measure', 'dimension'],
];

/** The selection bar's commands, grouped. */
export const MODIFY_GROUPS: readonly (readonly GeometriCommand[])[] = [
  ['move', 'copy', 'rotate', 'mirror', 'offset'],
  ['split', 'merge', 'join', 'trim', 'align'],
];

export const GEOMETRI_COMMANDS: readonly GeometriCommandDef[] = [
  { command: 'tool:select', label: 'Vælg', keys: 'V', keywords: 'markér pil select', ready: true },
  { command: 'tool:wall', label: 'Væg', keys: 'W', keywords: 'tegn væg linje rektangel planspor kæde', ready: true, write: true },
  { command: 'tool:floor', label: 'Dæk / gulv', keys: 'G', keywords: 'etageadskillelse gulv dæk', ready: false, later: 'S7', write: true },
  { command: 'tool:room', label: 'Rum', keys: 'R', keywords: 'rum placér lukket område polygon', ready: false, later: 'S6', write: true },
  { command: 'tool:zone', label: 'Zone', keys: 'Z', keywords: 'zone gruppe rum brandcelle', ready: false, later: 'S6', write: true },
  { command: 'tool:door', label: 'Dør', keys: 'D', keywords: 'dør åbning slår', ready: false, later: 'S5', write: true },
  { command: 'tool:window', label: 'Vindue', keys: 'N', keywords: 'vindue rude', ready: false, later: 'S5', write: true },
  { command: 'tool:opening', label: 'Åbning', keys: 'O', keywords: 'hul åbning gennembrud', ready: false, later: 'S5', write: true },
  { command: 'tool:stair', label: 'Trappe', keys: 'T', keywords: 'trappe trin løb repos', ready: false, later: 'S8', write: true },
  { command: 'tool:measure', label: 'Mål', keys: 'M', keywords: 'målebånd afstand hjælpelinje', ready: false, later: 'S9' },
  { command: 'tool:dimension', label: 'Målsætning', keys: '⇧M', keywords: 'målkæde målsætning dimension', ready: false, later: 'S9' },
  { command: 'move', label: 'Flyt', keys: 'MV', keywords: 'flyt forskyd move', ready: true, needsSelection: true, write: true },
  { command: 'copy', label: 'Kopiér', keys: 'CO', keywords: 'kopiér dublér copy', ready: true, needsSelection: true, write: true },
  { command: 'rotate', label: 'Rotér', keys: 'RO', keywords: 'rotér drej rotate', ready: true, needsSelection: true, write: true },
  { command: 'mirror', label: 'Spejl', keys: 'MM', keywords: 'spejl mirror', ready: true, needsSelection: true, write: true },
  { command: 'offset', label: 'Forskyd', keys: 'OF', keywords: 'parallel offset forskyd kopi afstand', ready: true, needsSelection: true, write: true },
  { command: 'split', label: 'Del', keys: 'SL', keywords: 'del op split klip væg', ready: true, needsSelection: true, write: true },
  { command: 'merge', label: 'Sammenføj', keys: 'SF', keywords: 'sammenføj flet merge forlæng på linje', ready: true, needsSelection: true, write: true },
  { command: 'join', label: 'Saml hjørne', keys: 'JN', keywords: 'hjørne saml forbind join corner', ready: true, needsSelection: true, write: true },
  { command: 'trim', label: 'Forlæng / trim', keys: 'TR', keywords: 'forlæng trim extend afkort grænse', ready: true, needsSelection: true, write: true },
  { command: 'align', label: 'Juster', keys: 'AL', keywords: 'juster align referencelinje planspor flugt', ready: true, needsSelection: true, write: true },
  { command: 'revert', label: 'Tilbage til detekteret', keys: '', keywords: 'nulstil gendan detekteret revert', ready: true, needsSelection: true, write: true },
  { command: 'delete', label: 'Slet', keys: 'Del', keywords: 'slet fjern fantomvæg delete', ready: true, needsSelection: true, write: true },
  { command: 'undo', label: 'Fortryd', keys: '⌘Z', keywords: 'fortryd undo', ready: true, write: true },
  { command: 'redo', label: 'Gentag', keys: '⇧⌘Z', keywords: 'gentag annullér fortryd redo', ready: true, write: true },
  { command: 'cycle', label: 'Næste overlappende', keys: 'Tab', keywords: 'skift overlappende element', ready: true },
  { command: 'view:plan', label: 'Vis plan', keys: '', keywords: 'plan ovenfra', ready: true },
  { command: 'view:3d', label: 'Vis 3D', keys: '', keywords: '3d model', ready: true },
  { command: 'zoom-fit', label: 'Zoom til alt', keys: '', keywords: 'zoom tilpas hele', ready: true },
  { command: 'rederive', label: 'Genberegn dæk og tag', keys: '', keywords: 'genberegn dæk tag solids afled', ready: true, write: true },
];

const BY_COMMAND = new Map(GEOMETRI_COMMANDS.map((c) => [c.command, c]));

export function commandDef(command: GeometriCommand): GeometriCommandDef {
  const def = BY_COMMAND.get(command);
  if (!def) throw new Error(`unknown geometri command ${command}`);
  return def;
}

/** A tooltip: "Væg (W)", or "Væg (W) — kommer snart" when it is not ready. */
export function commandTitle(command: GeometriCommand): string {
  const d = commandDef(command);
  const base = d.keys ? `${d.label} (${d.keys})` : d.label;
  return d.ready ? base : `${base} — kommer snart`;
}

/** The two-letter codes; their first letters wait for a second key. */
const CODES: Record<string, GeometriCommand> = {
  MV: 'move',
  CO: 'copy',
  RO: 'rotate',
  MM: 'mirror',
  OF: 'offset',
  SL: 'split',
  SF: 'merge',
  JN: 'join',
  TR: 'trim',
  AL: 'align',
};

/** Single letters (no modifier). */
const LETTERS: Record<string, GeometriCommand> = {
  V: 'tool:select',
  W: 'tool:wall',
  G: 'tool:floor',
  R: 'tool:room',
  Z: 'tool:zone',
  D: 'tool:door',
  N: 'tool:window',
  O: 'tool:opening',
  T: 'tool:stair',
  M: 'tool:measure',
};

const PREFIXES = new Set(Object.keys(CODES).map((c) => c[0]));

/** How long a code's first letter waits for the second, ms. */
export const CODE_WAIT_MS = 600;

export interface KeyIn {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  /** `kindOf(event.target)` (`app/keyTargets`): letters never fire while typing. */
  kind: TargetKind;
}

export interface KeyOut {
  /** The commands to run now, in order (a buffered letter that turned out to be no code, then this key's). */
  commands: GeometriCommand[];
  /** The letters typed so far of a two-letter code, waiting for the next key (or the timeout). */
  buffer: string;
  /** The key was ours: the caller prevents its default (Tab, Delete, ⌘Z). */
  consumed: boolean;
}

const NONE: KeyOut = { commands: [], buffer: '', consumed: false };

const out = (commands: (GeometriCommand | null | undefined)[], buffer = '', consumed = true): KeyOut => ({
  commands: commands.filter((c): c is GeometriCommand => Boolean(c)),
  buffer,
  consumed,
});

/**
 * One key press against the code buffer. ⌘Z / Ctrl+Z undo, ⇧⌘Z / Ctrl+Y
 * redo, Delete / Backspace delete, Tab cycles (⇧Tab back), Esc escapes;
 * letters pick tools or build a two-letter code. A field owns its keys
 * (only ⌘Z there is the field's own undo, so it is left alone too).
 */
export function resolveKeys(k: KeyIn, buffer: string): KeyOut {
  if (k.kind === 'text' || k.kind === 'choice') return NONE;
  const mod = k.metaKey || k.ctrlKey;
  const key = k.key.length === 1 ? k.key.toUpperCase() : k.key;
  if (mod && !k.altKey) {
    if (key === 'Z') return out([k.shiftKey ? 'redo' : 'undo']);
    if (key === 'Y' && k.ctrlKey) return out(['redo']);
    return NONE;
  }
  if (mod || k.altKey) return NONE;
  if (key === 'Escape') return out(['escape'], '', buffer !== '');
  if (key === 'Delete' || key === 'Backspace') return out(['delete']);
  if (key === 'Tab') return out([k.shiftKey ? 'cycle-back' : 'cycle']);
  if (key.length !== 1 || !/[A-ZÆØÅ]/.test(key)) return NONE;
  if (k.shiftKey) return key === 'M' && buffer === '' ? out(['tool:dimension']) : NONE;
  if (buffer !== '') {
    const code = CODES[buffer + key];
    if (code) return out([code]);
    // Not a code: the buffered letter counts alone, and this one starts afresh.
    const first = LETTERS[buffer];
    if (PREFIXES.has(key)) return out([first], key);
    return out([first, LETTERS[key]]);
  }
  if (PREFIXES.has(key)) return out([], key);
  const cmd = LETTERS[key];
  return cmd ? out([cmd]) : NONE;
}

/** The buffer's letter alone, once the wait for a second one ran out (null when it is no command). */
export function flushKeys(buffer: string): GeometriCommand | null {
  return buffer.length === 1 ? (LETTERS[buffer] ?? null) : null;
}

/** The tool hint of the status line: what to do, and the keys that help. */
export function toolHint(tool: GeometriTool, selected: number, wallMode: 'line' | 'rectangle' | 'trace' = 'line'): { lead: string; keys: { key: string; text: string }[] } {
  if (tool === 'wall') {
    return {
      lead: wallMode === 'trace' ? 'Væg · klik et planspor' : wallMode === 'rectangle' ? 'Væg · klik første hjørne' : 'Væg · klik startpunkt',
      keys: [
        { key: '⇧', text: 'lås retning' },
        { key: 'Alt', text: 'uden snap' },
        { key: 'Esc', text: 'tilbage til Vælg' },
      ],
    };
  }
  if (tool !== 'select') return { lead: commandDef(`tool:${tool}`).label, keys: [] };
  if (selected === 0) {
    return {
      lead: 'Klik et element',
      keys: [
        { key: '⇧', text: 'tilføj' },
        { key: 'træk', text: 'boksvalg' },
        { key: 'Tab', text: 'skift mellem overlappende' },
      ],
    };
  }
  return {
    lead: selected === 1 ? '1 valgt' : `${selected} valgt`,
    keys: [
      { key: 'Del', text: 'slet' },
      { key: 'Tab', text: 'skift mellem overlappende' },
      { key: 'Esc', text: 'fravælg' },
    ],
  };
}
